import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { HumanReviewRequestsService } from 'src/services/human-review-requests.service'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import type { EHumanReviewTab, THumanReviewTab } from './human-review-requests.type'
import useHumanReviewList from './use-human-review-list'
import HumanReviewPatientList from './human-review-patient-list.component'
import HumanReviewPatientPanel from './human-review-patient-panel.component'
import HumanReviewFilters from './human-review-filters.component'
import HumanReviewEmptyState from './human-review-empty-state.component'
import { useMedEventFilterOptions } from './use-med-event-filter-options'
import { useHumanReviewSelection } from './use-human-review-selection'
import { useAutoSelectedPatient } from './use-auto-selected-patient'
import { resolveFilterSelection } from './human-review-filters.util'

type Props = {
  subject: EHumanReviewTab
  activeTab: THumanReviewTab
  onTabChange: (tab: EHumanReviewTab) => void
}

const HumanReviewTabLayout = ({ subject, activeTab, onTabChange }: Props) => {
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const {
    selectedPatientMedEventId,
    problemsFilter,
    resolutionsFilter,
    medEventIdsFilter,
    selectPatientMedEvent,
    updateFilters,
  } = useHumanReviewSelection()
  const { medEventGroups, allMedEventItems } = useMedEventFilterOptions()

  const allMedEventIds = allMedEventItems.map((medEventItem) => medEventItem.value)
  const { selectedProblems, selectedResolutions, selectedMedEventIds } = resolveFilterSelection({
    tab: activeTab,
    problems: problemsFilter,
    resolutions: resolutionsFilter,
    medEventIds: medEventIdsFilter,
    allMedEventIds,
  })

  const { items, isLoading, error, isPlaceholderData, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useHumanReviewList({
      subject,
      problemsFilters: selectedProblems,
      resolutionsFilters: selectedResolutions,
      medEventIdsFilters:
        allMedEventIds.length <= 1 || selectedMedEventIds.length === allMedEventIds.length
          ? []
          : selectedMedEventIds,
    })

  useAutoSelectedPatient({
    subject,
    items: isPlaceholderData ? undefined : items,
    selectedId: selectedPatientMedEventId,
    onSelect: selectPatientMedEvent,
  })

  const {
    data: humanReviewDetail,
    isLoading: isHumanReviewDetailLoading,
    error: detailError,
  } = useQuery({
    queryKey: ['human-review-requests-detail', project.id, selectedPatientMedEventId],
    queryFn: () => HumanReviewRequestsService.getDetail(project.id, selectedPatientMedEventId!),
    enabled: selectedPatientMedEventId !== null,
  })

  if (isLoading) return <div className="loading" />
  if (error) return <ErrorQueryHandler error={error} />

  const listItems = items ?? []
  const noMedEventsSelected = allMedEventItems.length > 1 && selectedMedEventIds.length === 0
  const nothingToProcess = activeTab.toProcessCount === 0 && selectedResolutions.length === 0

  return (
    <>
      <HumanReviewFilters
        activeTab={activeTab}
        medEventGroups={medEventGroups}
        allMedEventItems={allMedEventItems}
        selectedProblems={selectedProblems}
        selectedResolutions={selectedResolutions}
        selectedMedEventIds={selectedMedEventIds}
        onSelectStatuses={updateFilters}
        onSelectMedEvents={(medEventIds) => updateFilters({ medEventIds })}
      />
      <div className="c-flex c-flex-grow c-border-t c-border-grey-300">
        {nothingToProcess ? (
          <HumanReviewEmptyState />
        ) : noMedEventsSelected || listItems.length === 0 ? (
          <div className="c-flex c-flex-col c-items-center c-justify-center c-w-full c-py-16 c-gap-4">
            <img src="/assets/img/no-patient-med-event.svg" alt="" className="c-w-48" />
            <p className="c-font-bold c-text-base c-text-grey-600">
              {t('pages.dashboard.human-review-requests.filters.no-results-message')}
            </p>
          </div>
        ) : (
          <>
            <HumanReviewPatientList
              items={listItems}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              selectedPatientMedEventId={selectedPatientMedEventId}
              onSelect={selectPatientMedEvent}
              onLoadMore={fetchNextPage}
            />
            {detailError && <ErrorQueryHandler error={detailError} />}
            {isHumanReviewDetailLoading && <div className="loading" />}
            {humanReviewDetail && (
              <HumanReviewPatientPanel
                humanReviewDetail={humanReviewDetail}
                activeSubject={subject}
                onTabChange={onTabChange}
              />
            )}
          </>
        )}
      </div>
    </>
  )
}

export default HumanReviewTabLayout
